/**
 * useFavoriteProductSheet Hook
 *
 * Resolves full product data from stores when a favorite product sheet is opened,
 * manages customization option selection state, computes price, and provides
 * action handlers (Add to Order, Update Favorite, Unfavorite).
 *
 * Follows the customization state pattern from MenuPage.tsx.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useRestaurantStore,
  useCartStore,
  useUIStore,
  useAuthStore,
  formatCurrency,
  OptionRow,
  getSupabaseClient,
} from '@restaurant/core'
import type {
  SheetContentFavoriteProductProps,
  SectionProductInformationProps,
  ProductOptionGroup,
  MenuItem,
} from '@restaurant/core'
import { WebFavoritesPageService } from '@restaurant/viewmodels'

// ── Raw DB option shape (stored by MenuPage's toggleFavorite) ───────────
interface RawSelectedOption {
  customizationId: string
  optionId: string
  name: string
}

// ── Helpers ─────────────────────────────────────────────────────────────

/**
 * Convert the raw `selected_options` array from the DB into the
 * `Record<string, Set<string>>` format used for customization state.
 *
 * Falls back gracefully if the raw data doesn't match the expected shape
 * (e.g. empty array, missing fields) by matching option *names* against
 * the menu item's customization definitions.
 */
function buildInitialSelections(
  raw: Record<string, unknown>[],
  menuItem: MenuItem,
): Record<string, Set<string>> {
  const result: Record<string, Set<string>> = {}

  if (!raw || raw.length === 0 || !menuItem.customizations) return result

  for (const entry of raw) {
    const opt = entry as Partial<RawSelectedOption>

    // Primary path: raw entry has customizationId + optionId
    if (opt.customizationId && opt.optionId) {
      const group = menuItem.customizations.find(c => c.id === opt.customizationId)
      if (group) {
        const option = group.options.find(o => o.id === opt.optionId)
        if (option) {
          const set = result[opt.customizationId] ?? (result[opt.customizationId] = new Set())
          set.add(opt.optionId)
          continue
        }
      }
    }

    // Fallback path: match by name against all customization groups
    if (opt.name) {
      for (const group of menuItem.customizations) {
        const match = group.options.find(
          o => o.name.toLowerCase() === (opt.name as string).toLowerCase(),
        )
        if (match) {
          const set = result[group.id] ?? (result[group.id] = new Set())
          set.add(match.id)
          break
        }
      }
    }
  }

  return result
}

/**
 * Deep-compare two selection maps for equality.
 */
function areSelectionsEqual(
  a: Record<string, Set<string>>,
  b: Record<string, Set<string>>,
): boolean {
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)

  // Collect all keys that have non-empty sets
  const activeA = keysA.filter(k => (a[k]?.size ?? 0) > 0)
  const activeB = keysB.filter(k => (b[k]?.size ?? 0) > 0)

  if (activeA.length !== activeB.length) return false

  for (const key of activeA) {
    const setA = a[key]
    const setB = b[key]
    if (!setA || !setB) return false
    if (setA.size !== setB.size) return false
    for (const v of setA) {
      if (!setB.has(v)) return false
    }
  }

  return true
}

// ── Hook ────────────────────────────────────────────────────────────────

export function useFavoriteProductSheet(): {
  sheetProps: SheetContentFavoriteProductProps | null
  isOpen: boolean
  onClose: () => void
} {
  const navigate = useNavigate()

  // Store selectors
  const isFavoriteProductSheetOpen = useUIStore(s => s.isFavoriteProductSheetOpen)
  const selectedFavoriteProduct = useUIStore(s => s.selectedFavoriteProduct)
  const closeFavoriteProductSheet = useUIStore(s => s.closeFavoriteProductSheet)
  const updateSelectedFavoriteOptions = useUIStore(s => s.updateSelectedFavoriteOptions)
  const showToast = useUIStore(s => s.showToast)
  const favoritesCount = useUIStore(s => s.favoritesCount)
  const setFavoritesCount = useUIStore(s => s.setFavoritesCount)
  const invalidateFavoritesCount = useUIStore(s => s.invalidateFavoritesCount)

  const getMenuItemById = useRestaurantStore(s => s.getMenuItemById)
  const addItem = useCartStore(s => s.addItem)

  const user = useAuthStore(s => s.user)
  const currentRestaurant = useRestaurantStore(s => s.currentRestaurant)

  // Resolve the full MenuItem from the restaurant store
  const menuItem = useMemo(
    () => (selectedFavoriteProduct ? getMenuItemById(selectedFavoriteProduct.productId) : undefined),
    [selectedFavoriteProduct, getMenuItemById],
  )

  // Memoised favorites service
  const favoritesService = useMemo(() => new WebFavoritesPageService(), [])

  // ── Customization state ─────────────────────────────────────────────
  const [selectedCustomizations, setSelectedCustomizations] = useState<Record<string, Set<string>>>({})
  const [initialSelections, setInitialSelections] = useState<Record<string, Set<string>>>({})

  // Re-initialise when a new favorite is opened
  useEffect(() => {
    if (selectedFavoriteProduct && menuItem) {
      const initial = buildInitialSelections(
        selectedFavoriteProduct.rawSelectedOptions,
        menuItem,
      )
      setSelectedCustomizations(initial)
      setInitialSelections(initial)
    }
  }, [selectedFavoriteProduct?.favoriteId, menuItem])

  // Track whether the user has changed options from the saved state
  const hasOptionsChanged = useMemo(
    () => !areSelectionsEqual(initialSelections, selectedCustomizations),
    [initialSelections, selectedCustomizations],
  )

  // Handle customisation option toggling (radio / checkbox)
  const handleOptionChange = useCallback(
    (customizationId: string, optionId: string, selected: boolean, isRadio: boolean) => {
      setSelectedCustomizations(prev => {
        const next = { ...prev }

        if (isRadio) {
          next[customizationId] = new Set([optionId])
        } else {
          const current = new Set(prev[customizationId] || [])
          if (selected) {
            current.add(optionId)
          } else {
            current.delete(optionId)
          }
          next[customizationId] = current
        }

        return next
      })
    },
    [],
  )

  // ── Option groups (for the sheet UI) ────────────────────────────────
  const optionGroups: ProductOptionGroup[] = useMemo(() => {
    if (!menuItem?.customizations) return []

    return menuItem.customizations.map(customization => {
      const isRadio = customization.maxSelections === 1
      const selections = selectedCustomizations[customization.id] || new Set()

      return {
        id: customization.id,
        title: customization.name,
        required: customization.isRequired,
        maxSelections: customization.maxSelections,
        children: React.createElement(
          React.Fragment,
          null,
          ...customization.options.map(option =>
            React.createElement(OptionRow, {
              key: option.id,
              text: `${option.name}${option.price > 0 ? ` (+${formatCurrency(option.price)})` : ''}`,
              optionType: isRadio ? 'radio' as const : 'checkbox' as const,
              value: option.id,
              name: customization.id,
              selected: selections.has(option.id),
              onChange: (value: string, selected: boolean) =>
                handleOptionChange(customization.id, value, selected, isRadio),
            }),
          ),
        ),
      }
    })
  }, [menuItem, selectedCustomizations, handleOptionChange])

  // ── Price computation ───────────────────────────────────────────────
  const totalPrice = useMemo(() => {
    if (!menuItem) return 0

    // Determine base price — use variant if applicable
    let base = menuItem.price
    const variant = selectedFavoriteProduct?.selectedPriceVariant
    if (variant && variant !== 'base' && menuItem.priceVariants?.[variant] != null) {
      base = menuItem.priceVariants[variant] as number
    }

    // Add customization option costs
    let customizationCost = 0
    for (const [groupId, optionIds] of Object.entries(selectedCustomizations)) {
      const group = menuItem.customizations?.find(c => c.id === groupId)
      if (!group) continue
      for (const optId of optionIds) {
        const opt = group.options.find(o => o.id === optId)
        if (opt) customizationCost += opt.price
      }
    }

    return base + customizationCost
  }, [menuItem, selectedCustomizations, selectedFavoriteProduct])

  // ── Persist helper (shared by Update Favorite and Add to Order) ─────

  /** Save current selections to the DB and sync local state. */
  const persistFavoriteOptions = useCallback(async () => {
    if (!selectedFavoriteProduct || !user?.id || !currentRestaurant?.id) return

    const newSelectedOptions = Object.entries(selectedCustomizations).flatMap(
      ([groupId, optionIds]) => {
        const group = menuItem?.customizations?.find(c => c.id === groupId)
        return Array.from(optionIds).map(optId => {
          const opt = group?.options.find(o => o.id === optId)
          return {
            customizationId: groupId,
            optionId: optId,
            name: opt?.name || '',
          }
        })
      },
    )

    const supabase = getSupabaseClient()
    await supabase
      .from('user_favorites')
      .update({ selected_options: newSelectedOptions })
      .eq('id', selectedFavoriteProduct.favoriteId)

    // Sync local state so hasOptionsChanged resets to false
    setInitialSelections(selectedCustomizations)
    // Update UIStore so reopening the sheet reflects the saved options
    updateSelectedFavoriteOptions(newSelectedOptions)
    // Trigger favorites page to re-fetch from API
    invalidateFavoritesCount()
  }, [selectedFavoriteProduct, selectedCustomizations, menuItem, user, currentRestaurant, updateSelectedFavoriteOptions, invalidateFavoritesCount])

  // ── Action handlers ─────────────────────────────────────────────────

  const handleAddToOrder = useCallback(async () => {
    if (!menuItem) return

    // If the user changed options, persist them before adding to cart
    if (hasOptionsChanged) {
      await persistFavoriteOptions()
    }

    const customizations = Object.entries(selectedCustomizations).flatMap(
      ([customizationItemId, optionIds]) =>
        Array.from(optionIds).map(selectedOptionId => ({
          customizationItemId,
          selectedOptionId,
        })),
    )

    addItem(
      menuItem,
      1,
      customizations,
      selectedFavoriteProduct?.selectedPriceVariant && selectedFavoriteProduct.selectedPriceVariant !== 'base'
        ? { selectedPriceVariant: selectedFavoriteProduct.selectedPriceVariant }
        : undefined,
    )

    closeFavoriteProductSheet()
    showToast('Added to order')
  }, [menuItem, selectedCustomizations, selectedFavoriteProduct, hasOptionsChanged, persistFavoriteOptions, addItem, closeFavoriteProductSheet, showToast])

  const handleUpdateFavorite = useCallback(async () => {
    await persistFavoriteOptions()
    showToast('Favorite updated')
  }, [persistFavoriteOptions, showToast])

  const handleUnfavorite = useCallback(async () => {
    if (!selectedFavoriteProduct || !user?.id) return

    await favoritesService.removeFavorite(selectedFavoriteProduct.favoriteId, user.id)
    closeFavoriteProductSheet()

    // Optimistic count update + trigger re-fetch for accuracy
    const newCount = Math.max(0, favoritesCount - 1)
    setFavoritesCount(newCount)
    invalidateFavoritesCount()

    if (newCount === 0) {
      // Last favorite removed — navigate to account page
      navigate('/account')
    }

    showToast('Removed from favorites')
  }, [selectedFavoriteProduct, user, closeFavoriteProductSheet, favoritesCount, setFavoritesCount, invalidateFavoritesCount, navigate, showToast, favoritesService])

  // ── Build return value ──────────────────────────────────────────────

  if (!selectedFavoriteProduct || !menuItem) {
    return {
      sheetProps: null,
      isOpen: isFavoriteProductSheetOpen,
      onClose: closeFavoriteProductSheet,
    }
  }

  const sectionProductInformation: SectionProductInformationProps = {
    productName: selectedFavoriteProduct.productName || menuItem.name,
    description: menuItem.description || '',
    currentPrice: formatCurrency(totalPrice),
    imageUrl: selectedFavoriteProduct.productImageUrl || menuItem.imageUrl,
  }

  return {
    sheetProps: {
      sectionProductInformation,
      optionGroups,
      addToOrderPrice: formatCurrency(totalPrice),
      onAddToOrder: handleAddToOrder,
      updateFavoriteEnabled: hasOptionsChanged,
      onUpdateFavorite: handleUpdateFavorite,
      onUnfavorite: handleUnfavorite,
    },
    isOpen: isFavoriteProductSheetOpen,
    onClose: closeFavoriteProductSheet,
  }
}
